import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Grid, 
  Card, 
  CardContent, 
  Link, 
  Button,
  useTheme,
  Alert,
  CircularProgress
} from '@mui/material';
import { 
  Phone,
  Forum,
  HelpCenter,
  Build
} from '@mui/icons-material';

// Images
import csRepImage from '../../assets/images/cs-rep.png';
import monitorImage from '../../assets/images/haven-monitor.png';
import controllerImage from '../../assets/images/controller-no-shadow.png';
import homeownerContactImage from '../../assets/images/homeowner-contact.png';
import proContactImage from '../../assets/images/pro-contact.png';

const ContactPage: React.FC = () => {
  const theme = useTheme();
  const [formState, setFormState] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    city: '',
    zipCode: '',
    country: '',
    stateProvince: '',
    userType: '',
    interests: [] as string[],
    centralAirSystem: '',
    additionalComments: '',
    isSubmitting: false,
    submissionSuccess: false,
    submissionError: false,
    errorMessage: '',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormState(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    if (name === 'interests') {
      setFormState(prevState => ({
        ...prevState,
        interests: checked 
          ? [...prevState.interests, value]
          : prevState.interests.filter(interest => interest !== value)
      }));
    } else {
      setFormState(prevState => ({
        ...prevState,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormState(prevState => ({ ...prevState, isSubmitting: true, submissionError: false, submissionSuccess: false }));

    try {
      // Format form data for email
      const emailContent = `
        <h2>New Contact Form Submission</h2>
        <p><strong>Name:</strong> ${formState.firstName} ${formState.lastName}</p>
        <p><strong>Email:</strong> ${formState.email}</p>
        <p><strong>Phone:</strong> ${formState.phone || 'Not provided'}</p>
        <p><strong>Location:</strong> ${formState.city}, ${formState.stateProvince}, ${formState.country}</p>
        <p><strong>Zip/Postal Code:</strong> ${formState.zipCode}</p>
        <p><strong>User Type:</strong> ${formState.userType}</p>
        <p><strong>Interests:</strong> ${formState.interests.join(', ') || 'None selected'}</p>
        <p><strong>Has Central Air System:</strong> ${formState.centralAirSystem}</p>
        ${formState.additionalComments ? `<p><strong>Additional Comments:</strong><br>${formState.additionalComments}</p>` : ''}
      `;

      const mandrillPayload = {
        key: 'md-0hbkGwKEhBY5Z-5Q_ELcQA',
        message: {
          html: emailContent,
          text: emailContent.replace(/<[^>]*>/g, ''), // Strip HTML for text version
          subject: `New Contact Form Submission from ${formState.firstName} ${formState.lastName}`,
          from_email: 'noreply@haveniaq.com',
          from_name: 'HAVEN Contact Form',
          to: [
            {
              email: 'support@haveniaq.com',
              name: 'HAVEN Support',
              type: 'to'
            }
          ],
          headers: {
            'Reply-To': formState.email
          },
          important: false,
          track_opens: true,
          track_clicks: true,
          auto_text: true,
          auto_html: false,
          inline_css: true,
          url_strip_qs: false,
          preserve_recipients: false,
          view_content_link: false
        }
      };

      const response = await fetch('https://mandrillapp.com/api/1.0/messages/send.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(mandrillPayload),
      });

      const result = await response.json();

      if (response.ok && result[0]?.status === 'sent') {
        setFormState(prevState => ({ 
          ...prevState, 
          submissionSuccess: true, 
          isSubmitting: false,
          // Reset form
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          city: '',
          zipCode: '',
          country: '',
          stateProvince: '',
          userType: '',
          interests: [],
          centralAirSystem: '',
          additionalComments: ''
        }));
      } else {
        throw new Error(result[0]?.reject_reason || 'Email delivery failed');
      }
    } catch (error: any) {
      console.error('Form submission error:', error);
      setFormState(prevState => ({ 
        ...prevState, 
        submissionError: true, 
        errorMessage: error.message || 'Something went wrong. Please try again.', 
        isSubmitting: false 
      }));
    }
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #FFE529 100%)`,
          py: 8,
          color: 'white'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            {/* Left side - Text content */}
            <Grid item xs={12} md={8}>
              <Typography variant="h1" sx={{ 
                mb: 3, 
                fontWeight: 'bold', 
                textAlign: { xs: 'center', md: 'left' },
                fontSize: { xs: '2.5rem', md: '3.5rem' }
              }}>
                Get in touch
              </Typography>
              <Typography variant="h5" sx={{ 
                textAlign: { xs: 'center', md: 'left' }, 
                fontWeight: 'normal', 
                opacity: 0.95,
                mb: 3
              }}>
                Questions about the product? Get them answered the way you want.
              </Typography>
              <Box sx={{
                backgroundColor: 'rgba(0, 150, 136, 0.8)',
                p: 3,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                maxWidth: '500px',
                mx: { xs: 'auto', md: 0 }
              }}>
                <Box sx={{
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  width: 40,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Typography variant="h6" sx={{ 
                    color: theme.palette.primary.main, 
                    fontWeight: 'bold' 
                  }}>
                    ?
          </Typography>
                </Box>
                <Typography variant="body1" sx={{ 
                  fontSize: '1.1rem',
                  fontWeight: '500',
                  color: 'white'
                }}>
                  Our online knowledge base and community forum have tons of great info that should answer most FAQs.
          </Typography>
              </Box>
            </Grid>

            {/* Right side - Customer service representative image */}
            <Grid item xs={12} md={4}>
              <Box sx={{ 
                textAlign: 'center',
                display: { xs: 'none', md: 'block' }
              }}>
                <img 
                  src={csRepImage} 
                  alt="Customer Service Representative" 
                  style={{ 
                    width: '100%', 
                    maxWidth: '350px',
                    height: 'auto'
                  }} 
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Quick Access Resources */}
        <Box sx={{ mb: 8 }}>
          <Grid container spacing={4}>
            {/* Community Discussions */}
            <Grid item xs={12} md={4}>
              <Card sx={{ 
                height: '100%', 
                p: 3, 
                textAlign: 'center',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: theme.shadows[10],
                }
              }}>
                <CardContent>
                  <Forum sx={{ fontSize: 60, color: theme.palette.primary.main, mb: 2 }} />
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
                    Community Discussions
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                    Join our community forum for peer discussions and shared experiences
                  </Typography>
                  <Button
                    variant="outlined"
                    size="large"
                    href="https://www.facebook.com/groups/havenprocommunity"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                      fontWeight: 'bold',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                      },
                    }}
                  >
                    COMMUNITY FORUM
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* General Knowledge Base */}
            <Grid item xs={12} md={4}>
              <Card sx={{ 
                height: '100%', 
                p: 3, 
                textAlign: 'center',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: theme.shadows[10],
                }
              }}>
                <CardContent>
                  <HelpCenter sx={{ fontSize: 60, color: theme.palette.primary.main, mb: 2 }} />
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
                    For General & Homeowners
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                    Comprehensive knowledge base with answers to common questions
                  </Typography>
                  <Button
                    variant="outlined"
                    size="large"
                    href="https://support.haveniaq.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                      fontWeight: 'bold',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                      },
                    }}
                  >
                    GENERAL KNOWLEDGE BASE
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* Pro Help Center */}
            <Grid item xs={12} md={4}>
              <Card sx={{ 
                height: '100%', 
                p: 3, 
                textAlign: 'center',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: theme.shadows[10],
                }
              }}>
                <CardContent>
                  <Build sx={{ fontSize: 60, color: theme.palette.primary.main, mb: 2 }} />
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
                    For Professionals
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                    Professional resources, installation guides, and technical support
                  </Typography>
                  <Button
                    variant="outlined"
                    size="large"
                    href="https://support.pro.haveniaq.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                      fontWeight: 'bold',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                      },
                    }}
                  >
                    PRO HELP CENTER
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Most Popular Help Desk Articles for Pros */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" sx={{ 
            mb: 6, 
            fontWeight: 'bold', 
            color: theme.palette.primary.main, 
            textAlign: 'center' 
          }}>
            Most Popular Help Desk Articles for Pros
          </Typography>
          
          <Grid container spacing={4}>
            {/* Central Air Monitor */}
            <Grid item xs={12} md={6}>
              <Card sx={{ 
                backgroundColor: theme.palette.primary.main,
                color: 'white',
                height: '100%',
                minHeight: '400px'
              }}>
                <CardContent sx={{ p: 4 }}>
                  <Grid container spacing={3} alignItems="center">
                    {/* Device Image */}
            <Grid item xs={12} md={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <img 
                          src={monitorImage} 
                          alt="HAVEN Central Air Monitor" 
                          style={{ 
                            width: '100%', 
                            maxWidth: '120px',
                            height: 'auto'
                          }} 
                        />
                      </Box>
                    </Grid>
                    
                    {/* Device Title */}
                    <Grid item xs={12} md={8}>
                      <Typography variant="h5" sx={{ 
                        fontWeight: 'bold',
                        color: 'white',
                        mb: 3
                      }}>
                        HAVEN Central Air Monitor
                      </Typography>
                    </Grid>
                  </Grid>

                  {/* Links */}
                  <Box sx={{ mt: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ 
                        backgroundColor: 'white',
                        borderRadius: '50%',
                        width: 24,
                        height: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2
                      }}>
                        <Typography variant="body2" sx={{ color: theme.palette.primary.main, fontWeight: 'bold' }}>
                          ?
                        </Typography>
                      </Box>
                      <Button 
                        href="https://support.pro.haveniaq.com/hc/en-us/articles/360061667594-How-to-install-the-HAVEN-Central-Air-Monitor"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ 
                          color: 'white', 
                          textTransform: 'none',
                          fontWeight: 'bold',
                          justifyContent: 'flex-start',
                          p: 0
                        }}
                      >
                        HOW TO INSTALL
                      </Button>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ 
                        backgroundColor: 'white',
                        borderRadius: '50%',
                        width: 24,
                        height: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2
                      }}>
                        <Typography variant="body2" sx={{ color: theme.palette.primary.main, fontWeight: 'bold' }}>
                          i
                  </Typography>
                      </Box>
                      <Button 
                        href="https://support.pro.haveniaq.com/hc/en-us/search/click?data=BAh7DjoHaWRsKwip2XTVUwA6D2FjY291bnRfaWRpAxo8ojoJdHlwZUkiDGFydGljbGUGOgZFVDoIdXJsSSJpaHR0cHM6Ly9zdXBwb3J0LnByby5oYXZlbmlhcS5jb20vaGMvZW4tdXMvYXJ0aWNsZXMvMzYwMDYzNDkwNDczLUhBVkVOLUNlbnRyYWwtQWlyLU1vbml0b3ItU29mdC1SZXNldAY7CFQ6DnNlYXJjaF9pZEkiKWRkNTMzZGQ1LTNkNDYtNGQ0MC04MmM4LWU2OWE5MjMwYTY0OAY7CEY6CXJhbmtpCToLbG9jYWxlSSIKZW4tdXMGOwhUOgpxdWVyeUkiCnJlc2V0BjsIVDoScmVzdWx0c19jb3VudGkW--dba0d9f02aef71ac07c882dd0c30c8dfa12e6fd2"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ 
                          color: 'white', 
                          textTransform: 'none',
                          fontWeight: 'bold',
                          justifyContent: 'flex-start',
                          p: 0
                        }}
                      >
                        HOW TO SOFT RESET
                      </Button>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ 
                        backgroundColor: 'white',
                        borderRadius: '50%',
                        width: 24,
                        height: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2
                      }}>
                        <Typography variant="body2" sx={{ color: theme.palette.primary.main, fontWeight: 'bold' }}>
                          !
                  </Typography>
                      </Box>
                      <Button 
                        href="https://support.pro.haveniaq.com/hc/en-us/articles/1500003066361-HAVEN-Central-Air-Monitor-Hard-Reset"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ 
                          color: 'white', 
                          textTransform: 'none',
                          fontWeight: 'bold',
                          justifyContent: 'flex-start',
                          p: 0
                        }}
                      >
                        HOW TO HARD RESET
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Central Air Controller */}
            <Grid item xs={12} md={6}>
              <Card sx={{ 
                backgroundColor: '#455a64',
                color: 'white',
                height: '100%',
                minHeight: '400px'
              }}>
                <CardContent sx={{ p: 4 }}>
                  <Grid container spacing={3} alignItems="center">
                    {/* Device Image */}
            <Grid item xs={12} md={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <img 
                          src={controllerImage} 
                          alt="HAVEN Central Air Controller" 
                          style={{ 
                            width: '100%', 
                            maxWidth: '120px',
                            height: 'auto'
                          }} 
                        />
                      </Box>
                    </Grid>
                    
                    {/* Device Title */}
                    <Grid item xs={12} md={8}>
                      <Typography variant="h5" sx={{ 
                        fontWeight: 'bold',
                        color: 'white',
                        mb: 3
                      }}>
                        HAVEN Central Air Controller
                      </Typography>
                    </Grid>
                  </Grid>

                  {/* Links */}
                  <Box sx={{ mt: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ 
                        backgroundColor: theme.palette.primary.main,
                        borderRadius: '50%',
                        width: 24,
                        height: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2
                      }}>
                        <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold' }}>
                          ?
                        </Typography>
                      </Box>
                      <Button 
                        href="https://support.pro.haveniaq.com/hc/en-us/articles/1500010374841-How-to-install-the-HAVEN-Central-Air-Controller"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ 
                          color: 'white', 
                          textTransform: 'none',
                          fontWeight: 'bold',
                          justifyContent: 'flex-start',
                          p: 0
                        }}
                      >
                        HOW TO INSTALL
                      </Button>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ 
                        backgroundColor: theme.palette.primary.main,
                        borderRadius: '50%',
                        width: 24,
                        height: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2
                      }}>
                        <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold' }}>
                          i
                        </Typography>
                      </Box>
                      <Button 
                        href="https://support.pro.haveniaq.com/hc/en-us/search/click?data=BAh7DjoHaWRsKwiTDa3qAAQ6D2FjY291bnRfaWRpAxo8ojoJdHlwZUkiDGFydGljbGUGOgZFVDoIdXJsSSJtaHR0cHM6Ly9zdXBwb3J0LnByby5oYXZlbmlhcS5jb20vaGMvZW4tdXMvYXJ0aWNsZXMvNDQwMTk4MzcyMDg1MS1IQVZFTi1DZW50cmFsLUFpci1Db250cm9sbGVyLVNvZnQtUmVzZXQGOwhUOg5zZWFyY2hfaWRJIilkZDUzM2RkNS0zZDQ2LTRkNDAtODJjOC1lNjlhOTIzMGE2NDgGOwhGOglyYW5raQc6C2xvY2FsZUkiCmVuLXVzBjsIVDoKcXVlcnlJIgpyZXNldAY7CFQ6EnJlc3VsdHNfY291bnRpFg%3D%3D--682282884719d63b8e0990914a133b740d747107"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ 
                          color: 'white', 
                          textTransform: 'none',
                          fontWeight: 'bold',
                          justifyContent: 'flex-start',
                          p: 0
                        }}
                      >
                        HOW TO SOFT RESET
                      </Button>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ 
                        backgroundColor: theme.palette.primary.main,
                        borderRadius: '50%',
                        width: 24,
                        height: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2
                      }}>
                        <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold' }}>
                          !
                        </Typography>
                      </Box>
                      <Button 
                        href="https://support.pro.haveniaq.com/hc/en-us/articles/4410639666323-Controlling-Your-Blower-Motor-with-the-HAVEN-Central-Air-Controller"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ 
                          color: 'white', 
                          textTransform: 'none',
                          fontWeight: 'bold',
                          justifyContent: 'flex-start',
                          p: 0
                        }}
                      >
                        CONTROL YOUR BLOWER MOTOR
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Contact Information Section */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" sx={{ 
            mb: 6, 
            fontWeight: 'bold', 
            color: '#455a64', 
            textAlign: 'center' 
          }}>
            Can't find what you're looking for in our knowledge base or forum?
          </Typography>
          
          <Grid container spacing={4}>
            {/* Homeowner Support */}
            <Grid item xs={12} md={6}>
              <Card sx={{ 
                height: '100%', 
                p: 4, 
                textAlign: 'center',
                backgroundColor: '#f8f9fa'
              }}>
                <CardContent>
                  <Box sx={{ mb: 3 }}>
                    <img 
                      src={homeownerContactImage} 
                      alt="Contact Homeowner Support" 
                      style={{ 
                        width: '100%', 
                        maxWidth: '300px',
                        height: 'auto'
                      }} 
                    />
                  </Box>
                  <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold', color: '#455a64' }}>
                    Contact Homeowner Support
                  </Typography>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                    <Link href="mailto:support@haveniaq.com" sx={{ color: theme.palette.primary.main, textDecoration: 'none' }}>
                      support@haveniaq.com
                    </Link>
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
                    <Phone sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Toll-free: 1-888-96-HAVEN (or 1-888-964-2836)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Monday to Friday, 10AM–6PM EST / 7AM-3PM PST
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Pro Support */}
            <Grid item xs={12} md={6}>
              <Card sx={{ 
                height: '100%', 
                p: 4, 
                textAlign: 'center',
                backgroundColor: '#f8f9fa'
              }}>
                <CardContent>
                  <Box sx={{ mb: 3 }}>
                    <img 
                      src={proContactImage} 
                      alt="Contact Pro Support" 
                      style={{ 
                        width: '100%', 
                        maxWidth: '300px',
                        height: 'auto'
                      }} 
                    />
                  </Box>
                  <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold', color: '#455a64' }}>
                    Contact Pro Support
                  </Typography>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                    <Link href="mailto:prosupport@haveniaq.com" sx={{ color: theme.palette.primary.main, textDecoration: 'none' }}>
                      prosupport@haveniaq.com
                    </Link>
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
                    <Phone sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Toll-free: 1-833-4-IAQ-PRO (or 1-833-442-7776)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Monday to Friday, 10AM–6PM EST / 7AM-3PM PST
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Contact Form Section */}
        <Box sx={{ 
          py: 6, 
          backgroundColor: '#f8f9fa'
        }}>
          <Container maxWidth="md">
            <Typography variant="h3" sx={{ 
              mb: 2, 
              fontWeight: 'bold', 
              textAlign: 'center',
              color: '#455a64'
            }}>
              Or use the contact form below:
            </Typography>
            <Box sx={{ 
              textAlign: 'center', 
              mb: 4,
              borderBottom: '3px solid #FFE529',
              width: '100px',
              mx: 'auto'
            }} />
            
            <Box 
              component="form" 
              onSubmit={handleSubmit}
              sx={{ 
                backgroundColor: 'white',
                p: 4,
                borderRadius: 2,
                boxShadow: 3
              }}
            >
              <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                Fields marked with an <Box component="span" sx={{ color: 'red' }}>*</Box> are required
              </Typography>

              <Grid container spacing={3}>
                {/* First Name and Last Name */}
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                    First name <Box component="span" sx={{ color: 'red' }}>*</Box>
                  </Typography>
                  <Box
                    component="input"
                    type="text"
                    name="firstName"
                    value={formState.firstName}
                    onChange={handleInputChange}
                    required
                    sx={{
                      width: '100%',
                      p: 2,
                      border: '2px solid #ddd',
                      borderRadius: 1,
                      fontSize: '1rem',
                      '&:focus': {
                        outline: 'none',
                        borderColor: theme.palette.primary.main
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                    Last name <Box component="span" sx={{ color: 'red' }}>*</Box>
                  </Typography>
                  <Box
                    component="input"
                    type="text"
                    name="lastName"
                    value={formState.lastName}
                    onChange={handleInputChange}
                    required
                    sx={{
                      width: '100%',
                      p: 2,
                      border: '2px solid #ddd',
                      borderRadius: 1,
                      fontSize: '1rem',
                      '&:focus': {
                        outline: 'none',
                        borderColor: theme.palette.primary.main
                      }
                    }}
                  />
                </Grid>

                {/* Email */}
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                    Email <Box component="span" sx={{ color: 'red' }}>*</Box>
                  </Typography>
                  <Box
                    component="input"
                    type="email"
                    name="email"
                    value={formState.email}
                    onChange={handleInputChange}
                    required
                    sx={{
                      width: '100%',
                      p: 2,
                      border: '2px solid #ddd',
                      borderRadius: 1,
                      fontSize: '1rem',
                      '&:focus': {
                        outline: 'none',
                        borderColor: theme.palette.primary.main
                      }
                    }}
                  />
                </Grid>

                {/* Phone */}
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                    Phone
                  </Typography>
                  <Box
                    component="input"
                    type="tel"
                    name="phone"
                    value={formState.phone}
                    onChange={handleInputChange}
                    sx={{
                      width: '100%',
                      p: 2,
                      border: '2px solid #ddd',
                      borderRadius: 1,
                      fontSize: '1rem',
                      '&:focus': {
                        outline: 'none',
                        borderColor: theme.palette.primary.main
                      }
                    }}
                  />
                </Grid>

                {/* City and Zip */}
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                    City <Box component="span" sx={{ color: 'red' }}>*</Box>
                  </Typography>
                  <Box
                    component="input"
                    type="text"
                    name="city"
                    value={formState.city}
                    onChange={handleInputChange}
                    required
                    sx={{
                      width: '100%',
                      p: 2,
                      border: '2px solid #ddd',
                      borderRadius: 1,
                      fontSize: '1rem',
                      '&:focus': {
                        outline: 'none',
                        borderColor: theme.palette.primary.main
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                    Zip / Postal code <Box component="span" sx={{ color: 'red' }}>*</Box>
                  </Typography>
                  <Box
                    component="input"
                    type="text"
                    name="zipCode"
                    value={formState.zipCode}
                    onChange={handleInputChange}
                    required
                    sx={{
                      width: '100%',
                      p: 2,
                      border: '2px solid #ddd',
                      borderRadius: 1,
                      fontSize: '1rem',
                      '&:focus': {
                        outline: 'none',
                        borderColor: theme.palette.primary.main
                      }
                    }}
                  />
                </Grid>

                {/* Country */}
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                    Country <Box component="span" sx={{ color: 'red' }}>*</Box>
                  </Typography>
                  <Box
                    component="select"
                    name="country"
                    value={formState.country}
                    onChange={handleInputChange}
                    required
                    sx={{
                      width: '100%',
                      p: 2,
                      border: '2px solid #ddd',
                      borderRadius: 1,
                      fontSize: '1rem',
                      backgroundColor: 'white',
                      '&:focus': {
                        outline: 'none',
                        borderColor: theme.palette.primary.main
                      }
                    }}
                  >
                    <option value="">- Select Country -</option>
                    <option value="Canada">Canada</option>
                    <option value="United States">United States</option>
                    <option value="Other">Other</option>
                  </Box>
                </Grid>

                {/* State/Province */}
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                    State / Province <Box component="span" sx={{ color: 'red' }}>*</Box>
                  </Typography>
                  <Box
                    component="select"
                    name="stateProvince"
                    value={formState.stateProvince}
                    onChange={handleInputChange}
                    required
                    sx={{
                      width: '100%',
                      p: 2,
                      border: '2px solid #ddd',
                      borderRadius: 1,
                      fontSize: '1rem',
                      backgroundColor: 'white',
                      '&:focus': {
                        outline: 'none',
                        borderColor: theme.palette.primary.main
                      }
                    }}
                  >
                    <option value="">- Select State/Province -</option>
                    {/* US States */}
                    <option value="Alabama">Alabama</option>
                    <option value="Alaska">Alaska</option>
                    <option value="Arizona">Arizona</option>
                    <option value="Arkansas">Arkansas</option>
                    <option value="California">California</option>
                    <option value="Colorado">Colorado</option>
                    <option value="Connecticut">Connecticut</option>
                    <option value="Delaware">Delaware</option>
                    <option value="Florida">Florida</option>
                    <option value="Georgia">Georgia</option>
                    <option value="Hawaii">Hawaii</option>
                    <option value="Idaho">Idaho</option>
                    <option value="Illinois">Illinois</option>
                    <option value="Indiana">Indiana</option>
                    <option value="Iowa">Iowa</option>
                    <option value="Kansas">Kansas</option>
                    <option value="Kentucky">Kentucky</option>
                    <option value="Louisiana">Louisiana</option>
                    <option value="Maine">Maine</option>
                    <option value="Maryland">Maryland</option>
                    <option value="Massachusetts">Massachusetts</option>
                    <option value="Michigan">Michigan</option>
                    <option value="Minnesota">Minnesota</option>
                    <option value="Mississippi">Mississippi</option>
                    <option value="Missouri">Missouri</option>
                    <option value="Montana">Montana</option>
                    <option value="Nebraska">Nebraska</option>
                    <option value="Nevada">Nevada</option>
                    <option value="New Hampshire">New Hampshire</option>
                    <option value="New Jersey">New Jersey</option>
                    <option value="New Mexico">New Mexico</option>
                    <option value="New York">New York</option>
                    <option value="North Carolina">North Carolina</option>
                    <option value="North Dakota">North Dakota</option>
                    <option value="Ohio">Ohio</option>
                    <option value="Oklahoma">Oklahoma</option>
                    <option value="Oregon">Oregon</option>
                    <option value="Pennsylvania">Pennsylvania</option>
                    <option value="Rhode Island">Rhode Island</option>
                    <option value="South Carolina">South Carolina</option>
                    <option value="South Dakota">South Dakota</option>
                    <option value="Tennessee">Tennessee</option>
                    <option value="Texas">Texas</option>
                    <option value="Utah">Utah</option>
                    <option value="Vermont">Vermont</option>
                    <option value="Virginia">Virginia</option>
                    <option value="Washington">Washington</option>
                    <option value="West Virginia">West Virginia</option>
                    <option value="Wisconsin">Wisconsin</option>
                    <option value="Wyoming">Wyoming</option>
                    {/* Canadian Provinces */}
                    <option value="Alberta">Alberta</option>
                    <option value="British Columbia">British Columbia</option>
                    <option value="Manitoba">Manitoba</option>
                    <option value="New Brunswick">New Brunswick</option>
                    <option value="Newfoundland and Labrador">Newfoundland and Labrador</option>
                    <option value="Northwest Territories">Northwest Territories</option>
                    <option value="Nova Scotia">Nova Scotia</option>
                    <option value="Nunavut">Nunavut</option>
                    <option value="Ontario">Ontario</option>
                    <option value="Prince Edward Island">Prince Edward Island</option>
                    <option value="Quebec">Quebec</option>
                    <option value="Saskatchewan">Saskatchewan</option>
                    <option value="Yukon">Yukon</option>
                    <option value="Other">Other</option>
                  </Box>
                </Grid>

                {/* I'm a... */}
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                    I'm a... <Box component="span" sx={{ color: 'red' }}>*</Box>
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        component="input"
                        type="checkbox"
                        name="userType"
                        value="homeowner"
                        checked={formState.userType === 'homeowner'}
                        onChange={handleCheckboxChange}
                        sx={{ mr: 2, transform: 'scale(1.2)' }}
                      />
                      <Typography variant="body1">Homeowner</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        component="input"
                        type="checkbox"
                        name="userType"
                        value="hvac-professional"
                        checked={formState.userType === 'hvac-professional'}
                        onChange={handleCheckboxChange}
                        sx={{ mr: 2, transform: 'scale(1.2)' }}
                      />
                      <Typography variant="body1">HVAC professional</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        component="input"
                        type="checkbox"
                        name="userType"
                        value="hvac-sales-rep"
                        checked={formState.userType === 'hvac-sales-rep'}
                        onChange={handleCheckboxChange}
                        sx={{ mr: 2, transform: 'scale(1.2)' }}
                      />
                      <Typography variant="body1">HVAC sales rep</Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* I'd like detail on... */}
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                    I'd like detail on... <Box component="span" sx={{ color: 'red' }}>*</Box>
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        component="input"
                        type="checkbox"
                        name="interests"
                        value="technical-specs"
                        checked={formState.interests.includes('technical-specs')}
                        onChange={handleCheckboxChange}
                        sx={{ mr: 2, transform: 'scale(1.2)' }}
                      />
                      <Typography variant="body1">Technical specs</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        component="input"
                        type="checkbox"
                        name="interests"
                        value="how-to-buy"
                        checked={formState.interests.includes('how-to-buy')}
                        onChange={handleCheckboxChange}
                        sx={{ mr: 2, transform: 'scale(1.2)' }}
                      />
                      <Typography variant="body1">How to buy</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        component="input"
                        type="checkbox"
                        name="interests"
                        value="beta-tester"
                        checked={formState.interests.includes('beta-tester')}
                        onChange={handleCheckboxChange}
                        sx={{ mr: 2, transform: 'scale(1.2)' }}
                      />
                      <Typography variant="body1">I'm interested in becoming a beta tester</Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Do you have a Central Air System? */}
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                    Do you have a Central Air System? <Box component="span" sx={{ color: 'red' }}>*</Box>
                  </Typography>
                  <Box
                    component="select"
                    name="centralAirSystem"
                    value={formState.centralAirSystem}
                    onChange={handleInputChange}
                    required
                    sx={{
                      width: '100%',
                      p: 2,
                      border: '2px solid #ddd',
                      borderRadius: 1,
                      fontSize: '1rem',
                      backgroundColor: 'white',
                      '&:focus': {
                        outline: 'none',
                        borderColor: theme.palette.primary.main
                      }
                    }}
                  >
                    <option value="">- Select -</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                    <option value="Not Sure">Not Sure</option>
                  </Box>
                </Grid>

                {/* Anything you'd like to add? */}
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                    Anything you'd like to add?
          </Typography>
                  <Box
                    component="textarea"
                    name="additionalComments"
                    value={formState.additionalComments}
                    onChange={handleInputChange}
                    rows={6}
                    sx={{
                      width: '100%',
                      p: 2,
                      border: '2px solid #ddd',
                      borderRadius: 1,
                      fontSize: '1rem',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      '&:focus': {
                        outline: 'none',
                        borderColor: theme.palette.primary.main
                      }
                    }}
                  />
                </Grid>

                {/* Submit Button */}
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={formState.isSubmitting}
                    sx={{
                      backgroundColor: '#535f66',
                      color: 'white',
                      fontWeight: 'bold',
                      px: 4,
                      py: 1.5,
                      '&:hover': {
                        backgroundColor: '#3d4851',
                      },
                    }}
                  >
                    {formState.isSubmitting ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Submit'
                    )}
                  </Button>
                </Grid>
              </Grid>
            </Box>

            {formState.submissionSuccess && (
              <Alert severity="success" sx={{ mt: 3, textAlign: 'center' }}>
                Your message has been submitted successfully! We will get back to you as soon as possible.
              </Alert>
            )}
            {formState.submissionError && (
              <Alert severity="error" sx={{ mt: 3, textAlign: 'center' }}>
                {formState.errorMessage}
              </Alert>
            )}

            <Typography variant="body2" sx={{ 
              mt: 3, 
              textAlign: 'center',
              color: 'text.secondary'
            }}>
            We typically respond to inquiries within 24 hours during business days.
          </Typography>
          </Container>
        </Box>
      </Container>
    </Box>
  );
};

export default ContactPage; 